import { Scene, GameObjects } from 'phaser';
import { MenuButton } from '../ui/MenuButton';
import { storage } from '../core/Storage';
import { initLocalization, t } from '../i18n/Localization';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { audioManager } from '../core/CoreManagerAudio';

interface UpgradeInfo {
  id: string;
  nameKey: string;
  descriptionKey: string;
  levels: { cost: number; bonusKey: string }[];
  currentLevel: number;
}

export class ShipWorkshop extends Scene {
  private background: GameObjects.Image | null = null;
  private title: GameObjects.Text | null = null;
  private biomatterText: GameObjects.Text | null = null;
  private biomatter: number = 0;
  override rexUI!: UIPlugin;

  private upgrades: UpgradeInfo[] = [
    {
      id: 'motor',
      nameKey: 'ship_workshop.upgrade.motor.name',
      descriptionKey: 'ship_workshop.upgrade.motor.desc',
      levels: [
        { cost: 0, bonusKey: 'ship_workshop.upgrade.motor.bonus.base' },
        { cost: 500, bonusKey: 'ship_workshop.upgrade.motor.bonus.25' },
        { cost: 1500, bonusKey: 'ship_workshop.upgrade.motor.bonus.50' },
        { cost: 5000, bonusKey: 'ship_workshop.upgrade.motor.bonus.100' },
      ],
      currentLevel: 0,
    },
    {
      id: 'tank',
      nameKey: 'ship_workshop.upgrade.tank.name',
      descriptionKey: 'ship_workshop.upgrade.tank.desc',
      levels: [
        { cost: 0, bonusKey: 'ship_workshop.upgrade.tank.bonus.100' },
        { cost: 750, bonusKey: 'ship_workshop.upgrade.tank.bonus.200' },
        { cost: 2000, bonusKey: 'ship_workshop.upgrade.tank.bonus.300' },
        { cost: 6000, bonusKey: 'ship_workshop.upgrade.tank.bonus.500' },
      ],
      currentLevel: 0,
    },
    {
      id: 'shield',
      nameKey: 'ship_workshop.upgrade.shield.name',
      descriptionKey: 'ship_workshop.upgrade.shield.desc',
      levels: [
        { cost: 0, bonusKey: 'ship_workshop.upgrade.shield.bonus.100' },
        { cost: 1000, bonusKey: 'ship_workshop.upgrade.shield.bonus.150' },
        { cost: 3000, bonusKey: 'ship_workshop.upgrade.shield.bonus.200' },
        { cost: 8000, bonusKey: 'ship_workshop.upgrade.shield.bonus.300' },
      ],
      currentLevel: 0,
    },
  ];

  constructor() {
    super('ShipWorkshop');
  }

  init(): void {
    this.background = null;
    this.title = null;
    this.biomatterText = null;
  }

  async create(): Promise<void> {
    initLocalization(this);
    this.rexUI = (this as any).rexUI as UIPlugin;
    
    // Load biomatter from storage
    this.biomatter = await storage.getScore(); // Using score as biomatter for now

    // Configure camera with fade-in
    this.cameras.main.setBackgroundColor(0x0d1f33);
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 500,
      ease: 'Power2'
    });

    this.createUI();
  }

  private createUI(): void {
    const { width, height } = this.scale;

    // Dark overlay background
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6);

    // Background
    this.background = this.add.image(width / 2, height / 2, 'background');
    this.background.setDisplaySize(width, height);
    this.background.setAlpha(0.3);

    // Title with animation
    this.title = this.add.text(width / 2, 50, t('ship_workshop.title'), {
      fontFamily: 'Arial Black',
      fontSize: '48px',
      color: '#00ff00',
      stroke: '#003300',
      strokeThickness: 8,
    }).setOrigin(0.5);
    this.title.setAlpha(0);

    // Biomatter display with animation
    this.biomatterText = this.add.text(
      width / 2,
      110,
      t('ship_workshop.biomatter').replace('{value}', `${this.biomatter}`),
      {
      fontFamily: 'Arial',
      fontSize: '28px',
      color: '#ffff00',
      }
    ).setOrigin(0.5);
    this.biomatterText.setAlpha(0);

    // Upgrade cards with stagger animation
    const startY = 200;
    const spacing = 180;
    const upgradeCards: GameObjects.Container[] = [];

    this.upgrades.forEach((upgrade, index) => {
      const y = startY + index * spacing;
      const card = this.createUpgradeCard(width / 2, y, upgrade);
      upgradeCards.push(card);
    });

    // Back button
    new MenuButton({
      scene: this,
      x: width / 2,
      y: height - 60,
      text: t('ui.back_to_menu'),
      onClick: () => {
        audioManager.playUISound('click', this);
        this.scene.start('MainMenu');
      },
    });

    // Animate everything in
    this.tweens.add({
      targets: this.title,
      alpha: 1,
      y: { from: 30, to: 50 },
      duration: 500,
      ease: 'Back.easeOut'
    });

    this.tweens.add({
      targets: this.biomatterText,
      alpha: 1,
      duration: 400,
      delay: 200,
      ease: 'Power2'
    });

    upgradeCards.forEach((card, idx) => {
      card.setAlpha(0);
      this.tweens.add({
        targets: card,
        alpha: 1,
        duration: 400,
        delay: 300 + idx * 100,
        ease: 'Power2'
      });
    });

    // Update layout on resize
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      const { width, height } = gameSize;
      this.updateLayout(width, height, upgradeCards);
    });
  }

  private createUpgradeCard(x: number, y: number, upgrade: UpgradeInfo): GameObjects.Container {
    const cardWidth = 700;
    const cardHeight = 150;
    const container = this.add.container(x, y);

    // Card background with RexUI roundRectangle
    const card = this.rexUI.add.roundRectangle(0, 0, cardWidth, cardHeight, 12, 0x222222)
      .setStrokeStyle(3, 0x00ff00);
    container.add(card);

    // Upgrade name
    const nameText = this.add.text(-cardWidth / 2 + 20, -50, t(upgrade.nameKey), {
      fontFamily: 'Arial Black',
      fontSize: '28px',
      color: '#00ff00',
    });
    container.add(nameText);

    // Description
    const descText = this.add.text(-cardWidth / 2 + 20, -15, t(upgrade.descriptionKey), {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#888888',
    });
    container.add(descText);

    // Level indicators
    const levelStartX = -cardWidth / 2 + 20;
    const levelY = 35;

    upgrade.levels.forEach((level, levelIndex) => {
      const levelX = levelStartX + levelIndex * 160;
      const isUnlocked = levelIndex <= upgrade.currentLevel;
      const isNext = levelIndex === upgrade.currentLevel + 1;

      // Level box with RexUI
      const box = this.rexUI.add.roundRectangle(levelX + 60, levelY, 140, 40, 8, isUnlocked ? 0x00aa00 : 0x333333)
        .setStrokeStyle(2, isNext ? 0xffff00 : 0x555555);
      container.add(box);

      // Level text
      const levelText = this.add.text(levelX + 60, levelY - 8, t('ship_workshop.level_label').replace('{level}', `${levelIndex}`), {
        fontFamily: 'Arial',
        fontSize: '14px',
        color: isUnlocked ? '#ffffff' : '#666666',
      }).setOrigin(0.5);
      container.add(levelText);

      // Cost or status
      let statusText = '';
      let statusColor = '#666666';

      if (isUnlocked) {
        statusText = levelIndex === 0 ? t('ship_workshop.status.base') : t('ship_workshop.status.owned');
        statusColor = '#00ff00';
      } else if (isNext) {
        statusText = `${level.cost}`;
        statusColor = this.biomatter >= level.cost ? '#ffff00' : '#ff4444';
      } else {
        statusText = t('ship_workshop.status.locked');
      }

      const statusTextObj = this.add.text(levelX + 60, levelY + 10, statusText, {
        fontFamily: 'Arial',
        fontSize: '12px',
        color: statusColor,
      }).setOrigin(0.5);
      container.add(statusTextObj);

      // Make purchasable levels interactive
      if (isNext && this.biomatter >= level.cost) {
        box.setInteractive({ useHandCursor: true });
        
        const normalColor = 0x333333;
        const hoverColor = Phaser.Display.Color.ValueToColor(normalColor).lighten(30).color;
        const downColor = Phaser.Display.Color.ValueToColor(normalColor).darken(20).color;

        let isPressed = false;

        box.on('pointerover', () => {
          if (isPressed) return;
          box.setFillStyle(hoverColor);
          container.setScale(1.02);
          audioManager.playUISound('hover', this);
        });

        box.on('pointerout', () => {
          if (isPressed) return;
          box.setFillStyle(normalColor);
          container.setScale(1);
        });

        box.on('pointerdown', () => {
          isPressed = true;
          box.setFillStyle(downColor);
          container.setScale(0.98);
        });

        box.on('pointerup', () => {
          if (!isPressed) return;
          isPressed = false;
          audioManager.playUISound('click', this);
          this.purchaseUpgrade(upgrade, levelIndex, container);
        });

        box.on('pointerupoutside', () => {
          isPressed = false;
          box.setFillStyle(normalColor);
          container.setScale(1);
        });

        box.on('pointermove', (pointer: Phaser.Input.Pointer) => {
          if (!isPressed) return;
          const bounds = new Phaser.Geom.Rectangle(levelX + 60 - 70, levelY - 20, 140, 40);
          const isInBounds = Phaser.Geom.Rectangle.Contains(bounds, pointer.x - container.x, pointer.y - container.y);
          if (!isInBounds && isPressed) {
            isPressed = false;
            box.setFillStyle(normalColor);
            container.setScale(1);
          }
        });
      }
    });

    return container;
  }

  private async purchaseUpgrade(upgrade: UpgradeInfo, level: number, container: GameObjects.Container): Promise<void> {
    const levelData = upgrade.levels[level];
    if (!levelData) return;

    const cost = levelData.cost;

    if (this.biomatter >= cost) {
      this.biomatter -= cost;
      upgrade.currentLevel = level;

      // Purchase feedback animation
      this.tweens.add({
        targets: container,
        scale: { from: 1, to: 1.05 },
        duration: 150,
        ease: 'Back.easeOut',
        yoyo: true,
        onComplete: () => {
          // Flash green on successful purchase
          const originalColor = container.getAt(0) as any; // The card background
          const originalFill = originalColor.fillColor;
          originalColor.setFillStyle(0x00ff00);
          this.time.delayedCall(200, () => {
            originalColor.setFillStyle(originalFill);
          });
        }
      });

      // Save to storage
      await storage.saveScore(this.biomatter);

      // Update biomatter display
      if (this.biomatterText) {
        this.biomatterText.setText(t('ship_workshop.biomatter').replace('{value}', `${this.biomatter}`));
      }

      // Refresh UI after a short delay
      this.time.delayedCall(500, () => {
        this.scene.restart();
      });
    }
  }

  private updateLayout(width: number, height: number, upgradeCards: GameObjects.Container[]): void {
    // Resize camera viewport
    this.cameras.resize(width, height);

    // Stretch background
    if (this.background) {
      this.background.setDisplaySize(width, height);
    }

    // Compute scale factor
    const scaleFactor = Math.min(Math.min(width / 1024, height / 768), 1);

    // Position title
    if (this.title) {
      this.title.setPosition(width / 2, 50);
      this.title.setScale(scaleFactor);
    }

    // Position biomatter text
    if (this.biomatterText) {
      this.biomatterText.setPosition(width / 2, 110);
      this.biomatterText.setScale(scaleFactor);
    }

    // Position upgrade cards
    const startY = 200;
    const spacing = 180;
    upgradeCards.forEach((card, idx) => {
      const y = startY + idx * spacing;
      card.setPosition(width / 2, y);
      card.setScale(scaleFactor);
    });
  }
}
